import {attemptedQuestion} from './practiceProgress';
import {hasDescriptiveResponse} from '../../utils/starterSpreadsheet';
export const TYPE_LABELS = { mcq: 'Multiple choice', dropdown: 'Select an answer', 'short-answer': 'Short answer', matching: 'Matching pairs', 'mcq-grid': 'Answer grid', descriptive: 'Written response' };
export const topicName = question => String(question.topic || 'General practice').trim() || 'General practice';
export const topicKey = value => String(value || '').trim().toLowerCase();
export const plainText = value => {
    const doc = new DOMParser().parseFromString(String(value || ''), 'text/html');
    return (doc.body.textContent || '').replace(/\s+/g, ' ').trim();
};
export const safeUrl = value => { try { const url = new URL(value); return ['https:', 'http:'].includes(url.protocol) ? url.href : ''; } catch { return ''; } };
// Pasted screenshots are stored inside editor HTML as raster data URLs.
// Allow these only as image sources; never permit SVG, HTML or script data URLs.
export const safeImageUrl = value => {
    if (typeof value !== 'string') return '';
    const source = value.trim();
    if (/^data:image\/(?:png|jpe?g|gif|webp|avif|bmp);base64,[a-z0-9+/=\s]+$/i.test(source)) return source;
    return safeUrl(source);
};
// Keep document structure while removing scripts, handlers, styles and unsafe URLs.
export const cleanHtml = value => {
    const doc = new DOMParser().parseFromString(String(value || ''), 'text/html');
    const tags = new Set(['P','BR','DIV','SPAN','B','STRONG','I','EM','U','S','SUB','SUP','UL','OL','LI','H1','H2','H3','H4','TABLE','THEAD','TBODY','TR','TD','TH','BLOCKQUOTE','PRE','CODE','IMG']);
    const clean = node => {
        [...node.children].forEach(child => {
            if (['SCRIPT','STYLE','IFRAME','OBJECT','SVG','MATH','TEMPLATE'].includes(child.tagName)) { child.remove(); return; }
            clean(child);
            if (!tags.has(child.tagName)) { child.replaceWith(...child.childNodes); return; }
            [...child.attributes].forEach(attr => {
                const name = attr.name.toLowerCase();
                if (child.tagName === 'IMG' && name === 'src' && safeImageUrl(attr.value)) return;
                if (child.tagName === 'IMG' && name === 'alt') return;
                if (['TD','TH'].includes(child.tagName) && ['colspan','rowspan'].includes(name) && /^[1-9]\d?$/.test(attr.value)) return;
                child.removeAttribute(attr.name);
            });
        });
    };
    clean(doc.body); return doc.body.innerHTML;
};
export function questionImageSources(question) {
    const separate = Array.isArray(question.images) ? question.images : question.images ? [question.images] : [];
    const embedded = new DOMParser().parseFromString(cleanHtml(question.text), 'text/html');
    return [...new Set([...separate, ...Array.from(embedded.querySelectorAll('img[src]'), image => image.getAttribute('src'))].map(safeImageUrl).filter(Boolean))];
}
export function hasQuestionContent(question) {
    return Boolean(plainText(cleanHtml(question.text)) || questionImageSources(question).length);
}
const indexValid = (value, options) => value !== '' && value != null && Number.isInteger(Number(value)) && Number(value) >= 0 && Number(value) < (options || []).length;
export function validationError(q, topic) {
    if (!String(topic || '').trim()) return 'Choose a topic for this question.';
    if (!hasQuestionContent(q)) return 'Add question text or an image.';
    if (!TYPE_LABELS[q.type]) return 'Choose a supported question format.';
    if (!Number.isFinite(Number(q.marks)) || Number(q.marks) <= 0) return 'Marks must be greater than zero.';
    if (q.bunnyVideoId && !String(q.bunnyLibraryId || '').trim()) return 'The attached video needs a video library. Remove the video to publish now, or check the video settings.';
    return answerValidationError(q);
}
// Student checking depends on answer configuration, not publishing metadata.
export function answerValidationError(q) {
    if (!TYPE_LABELS[q.type]) return 'Choose a supported question format.';
    if (['mcq','dropdown','matching'].includes(q.type) && ((q.options || []).length < 2 || q.options.some(o => !String(o).trim()))) return 'Provide at least two non-empty options.';
    if (q.type === 'mcq') {
        const keys = Array.isArray(q.correctAnswer) ? q.correctAnswer : [q.correctAnswer];
        if (!keys.length || keys.some(k => !indexValid(k, q.options))) return 'Select a valid correct answer.';
    }
    if (q.type === 'dropdown' && !indexValid(q.correctAnswer, q.options)) return 'Select the correct dropdown answer.';
    if (q.type === 'short-answer' && !String(q.correctAnswer ?? '').trim()) return 'Enter the correct short answer.';
    if (q.type === 'matching' && (!(q.prompts || []).length || q.prompts.some((p,i) => !String(p).trim() || !indexValid(q.correctAnswer?.[i], q.options)))) return 'Every matching prompt needs text and a correct pair.';
    if (q.type === 'mcq-grid' && (!(q.rows || []).length || !(q.columns || []).length || q.columns.some(c => !String(c).trim()) || q.rows.some((r,i) => !String(r).trim() || !indexValid(q.correctAnswer?.[i], q.columns)))) return 'Every grid row needs text and a correct column.';
    return '';
}
export function answerReady(q, answer) {
    if (q.type === 'descriptive') return hasDescriptiveResponse(q, answer);
    if (q.type === 'mcq') return Array.isArray(answer) ? answer.length > 0 : indexValid(answer, q.options);
    if (q.type === 'dropdown') return indexValid(answer, q.options);
    if (q.type === 'matching' || q.type === 'mcq-grid') return (q.type === 'matching' ? q.prompts : q.rows)?.every((_,i) => indexValid(answer?.[i], q.type === 'matching' ? q.options : q.columns));
    return String(answer ?? '').trim().length > 0;
}
const normalize = value => {
    const s = String(value ?? '').trim().toLowerCase();
    const n = s.replace(/,/g, '');
    return /^[+-]?(?:\d+\.?\d*|\.\d+)$/.test(n) && Number.isFinite(Number(n)) ? String(Number(n)) : s;
};
export function evaluate(q, answer) {
    if (!answerReady(q, answer)) return 'unanswered';
    if (q.type === 'descriptive') return 'review';
    let correct = false;
    if (q.type === 'mcq') {
        const expected = (Array.isArray(q.correctAnswer) ? q.correctAnswer : [q.correctAnswer]).map(String).sort();
        const selected = (Array.isArray(answer) ? answer : [answer]).map(String).sort();
        correct = expected.length > 0 && JSON.stringify(expected) === JSON.stringify(selected);
    } else if (q.type === 'short-answer') correct = (Array.isArray(q.correctAnswer) ? q.correctAnswer : [q.correctAnswer]).some(a => normalize(a) !== '' && normalize(a) === normalize(answer));
    else if (q.type === 'dropdown') correct = String(answer) === String(q.correctAnswer);
    else if (q.type === 'matching' || q.type === 'mcq-grid') {
        const rows = q.type === 'matching' ? q.prompts : q.rows;
        correct = rows?.length > 0 && rows.every((_,i) => q.correctAnswer?.[i] != null && String(answer[i]) === String(q.correctAnswer[i]));
    }
    return correct ? 'correct' : 'incorrect';
}
export function filterQuestions(questions, { search = '', topic = '', type = '', difficulty = '', mode = 'all', saved = [], progress = {}, videoOnly = false } = {}) {
    const completed = new Set(progress.completedQuestions || []);
    const terms = search.toLowerCase().trim().split(/\s+/).filter(Boolean);
    return questions.filter(q => {
        const haystack = `${q.practiceNumber || q.questionNumber || ''} q${q.practiceNumber || q.questionNumber || ''} ${plainText(q.text)} ${topicName(q)} ${q.title || ''} ${q.scenario?.title || ''} ${plainText(q.scenario?.text)} ${(q.tags || []).join(' ')}`.toLowerCase();
        return terms.every(t => haystack.includes(t)) && (!topic || topicKey(topicName(q)) === topicKey(topic)) && (!type || q.type === type) && (!difficulty || (q.difficulty || 'standard') === difficulty) && (!videoOnly || (q.bunnyVideoId && q.bunnyLibraryId)) && (mode !== 'saved' || saved.includes(q.id)) && (mode !== 'new' || !completed.has(q.id)) && (mode !== 'unattempted' || !attemptedQuestion(progress,q.id)) && (mode !== 'mistakes' || progress.results?.[q.id]?.outcome === 'incorrect');
    });
}
export function applyOutcome(previous, question, outcome, questions) {
    const completed = new Set(previous.completedQuestions || []);
    if (['correct','reviewed'].includes(outcome)) completed.add(question.id);
    const topicCounts = new Map();
    questions.forEach(q => { if (completed.has(q.id)) { const topic = topicName(q); topicCounts.set(topic, (topicCounts.get(topic) || 0) + 1); } });
    const topics = Object.fromEntries(topicCounts);
    return { ...previous, completedQuestions: [...completed], totalCompleted: questions.filter(q => completed.has(q.id)).length, topics,
        results: { ...(previous.results || {}), [question.id]: { outcome, updatedAt: Date.now() } } };
}
export function buildQuestionEntry(q, { topic, paper, bunnyLibraryId, bunnyVideoId }) {
    const { id, isProcessing, suggestedAnswerView, createdAt, updatedAt, ...data } = q;
    const videoId = String(bunnyVideoId || '').trim();
    return { ...data, tags:(Array.isArray(data.tags) ? data.tags : []).map(t=>String(t).trim()).filter(Boolean), paperId: paper.id, paperName: paper.name || '', topic: topic.trim(), bunnyLibraryId: videoId ? String(bunnyLibraryId || '').trim() : '', bunnyVideoId: videoId };
}
export function removePairItem(q, field, index) {
    const list = q[field].filter((_,i) => i !== index); const correctAnswer = {};
    Object.entries(q.correctAnswer || {}).forEach(([key,value]) => {
        if (field === 'prompts' || field === 'rows') { if (Number(key) !== index) correctAnswer[Number(key) > index ? Number(key)-1 : key] = value; }
        else if (Number(value) !== index) correctAnswer[key] = Number(value) > index ? Number(value)-1 : value;
    });
    return { ...q, [field]: list, correctAnswer };
}
